import { useCallback, useEffect, useState } from 'react';
// Hooks
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';
// Utils
import { getAvailableRankings } from '../../../../packages/services/tennis/ranking';
// Types
import { IStandingsConfig } from '../../../../packages/types/standings';
import { IAvailableRanking, RankingPill } from '../../../../packages/types/tennisV2';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import { RefreshTimes, SDKOptions } from '../../../../packages/types';

interface IUseAvailableRankings extends IGeneralSubscriberInfo {
  dataStandingTypeSubcategory?: string;
  dataRefreshTime?: RefreshTimes;
  dataHeaderDefaultOption?: string;
  dataLabels?: IStandingsConfig['dataLabels'];
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'];
  dataTeamNameType?: IStandingsConfig['dataTeamNameType'];
  sdkOptions: SDKOptions;
}

export const useAvailableRankings = ({
  dataStandingTypeSubcategory,
  dataRefreshTime,
  dataWidgetCuid,
  dataWidgetId,
  dataWidgetSport,
  subscribers,
  sdkOptions,
}: IUseAvailableRankings) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [availableRankings, setAvailableRankings] = useState<IAvailableRanking[]>([]);
  const [selectedRanking, setSelectedRanking] = useState<RankingPill>({ id: '', label: '' });
  const [hasError, setHasError] = useState<boolean>(false);

  const params = { standingTypeSubcategory: dataStandingTypeSubcategory || '' };

  const fetchAvailableRankings = useCallback(
    (manageLoader: boolean) => {
      manageLoader && setIsLoading(true);
      setHasError(false);

      getAvailableRankings({ params, sdkOptions })
        ?.then((response) => {
          manageLoader && setIsLoading(false);

          if (response && response.data && response.data.length > 0) {
            setAvailableRankings(response.data);
            setSelectedRanking({
              id: response.data[0].id,
              label: response.data[0].standing_type?.name || '',
            });
          } else {
            setAvailableRankings([]);
            setSelectedRanking({ id: '', label: '' });
            setHasError(true);
          }
        })
        .catch((error: unknown) => {
          console.error('Error fetching available rankings:', error);
          manageLoader && setIsLoading(false);
          setAvailableRankings([]);
          setSelectedRanking({ id: '', label: '' });
          setHasError(true);
        });
    },
    [dataStandingTypeSubcategory],
  );

  const pillTabOptions =
    availableRankings?.map((option) => ({
      label: option.standing_type?.name || 'Unknown',
      value: option.id,
    })) || [];

  useFetchIntervalData(fetchAvailableRankings, dataRefreshTime);

  useEffect(() => {
    fetchAvailableRankings(true);
  }, [fetchAvailableRankings]);

  return {
    availableRankings,
    selectedRanking,
    pillTabOptions,
    setSelectedRanking,
    isLoading,
    hasError,
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
};
